import {useState} from 'react'
import {useSelector} from 'react-redux'
import {authorizationHeaderFromAccessToken} from '../auth'
import {API_URL} from '../config'
import {captureException} from '../errors'
import ServerError from '../errors/server'
import {getAccessToken} from '../store/authentication/authentication.selectors'

type Options = Readonly<{
  onError?: (error: Error) => void
  onSuccess?: () => void
}>

function useUpdateSponsorImage({onError, onSuccess}: Options = {}): {
  busy: boolean
  updateSponsorImage: (sponsorId: string, image: File) => void
  error: Error | null
} {
  const accessToken = useSelector(getAccessToken)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const updateSponsorImage = (sponsorId: string, image: File) => {
    setBusy(true)
    setError(null)

    fireUpdateSponsorImage(accessToken, sponsorId, image)
      .then(() => onSuccess?.())
      .catch(error => {
        captureException(error)
        setError(error)
        onError?.(error)
      })
      .finally(() => setBusy(false))
  }

  return {busy, updateSponsorImage, error}
}

async function fireUpdateSponsorImage(
  accessToken: string,
  sponsorId: string,
  image: File
): Promise<void> {
  const url = new URL(`sponsors/${sponsorId}/images`, API_URL)

  const formData = new FormData()
  formData.append('image', image)

  const response = await fetch(url.toString(), {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      ...authorizationHeaderFromAccessToken(accessToken)
    },
    body: formData
  })

  if (response.status !== 204) {
    throw await ServerError.fromResponse(response)
  }
}

export default useUpdateSponsorImage
